import i18n from 'i18next';
import { axios } from '@/axios';
import { config } from '@/config';

export async function getTagsFromCourse(
  nameCourse,
  list,
  existingTags = [],
  authId
) {
  if (!nameCourse && !list?.length) return [];

  let lang;

  if (i18n.language === 'en') {
    lang = 'English';
  } else if (i18n.language === 'fr') {
    lang = 'French';
  } else if (i18n.language === 'es') {
    lang = 'Spanish';
  } else if (i18n.language === 'de') {
    lang = 'German';
  } else {
    lang = 'Dutch';
  }

  const prompt = `
Analyze the course information below and generate relevant tags for the course.

Course name:
${nameCourse || ''}

Course content (lesson and folder names):
${JSON.stringify(list)}

Existing tags:
${JSON.stringify(existingTags)}

Rules:
- Return up to 10 relevant tags.
- Tags must be in ${lang}.
- Tags must describe the overall course, not individual lessons only.
- Use the course name and course content together to understand the main subjects and skills covered by the course.
- Prefer tags that represent the most important topics, concepts, skills, technologies, disciplines, or professional areas of the course.
- Tags must be short and concise.
- Prefer 1-3 words per tag.
- Do not duplicate existing tags.
- Do not return near-duplicate synonyms of existing tags.
- Do not return multiple tags that mean almost the same thing.
- Prefer specific tags over generic ones.
- Avoid generic tags such as:
  course, training, lesson, learning, education, module, content.
- Do not include hashtags.
- Do not include explanations.
- Return only a valid JSON object in this format:

{
  "tags": ["tag1", "tag2", "tag3"]
}
`;

  try {
    const dataOpenAI = await axios.post(`${config.backend}/openai`, {
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'developer',
          content:
            'You analyze course content and return only valid JSON. The response must contain a key "tags" with an array of strings.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      model: 'gpt-5.4-mini',
      temperature: 0.2,
      max_completion_tokens: 500,
      user: `${authId}`,
    });

    const fullAnswer = dataOpenAI?.data?.choices?.[0]?.message?.content || '';

    const jsonObject = JSON.parse(fullAnswer);

    const tags = Array.isArray(jsonObject)
      ? jsonObject
      : Array.isArray(jsonObject?.tags)
      ? jsonObject.tags
      : [];

    const existingLower = new Set(
      (existingTags || []).map((tag) => String(tag).trim().toLowerCase())
    );

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => !existingLower.has(tag.toLowerCase()))
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting course tags:', error);
    return [];
  }
}

export async function getTagsFromChapter(
  nameChapter,
  list,
  existingTags = [],
  authId
) {
  if (!nameChapter && !list?.length) return [];

  let lang;

  if (i18n.language === 'en') {
    lang = 'English';
  } else if (i18n.language === 'fr') {
    lang = 'French';
  } else if (i18n.language === 'es') {
    lang = 'Spanish';
  } else if (i18n.language === 'de') {
    lang = 'German';
  } else {
    lang = 'Dutch';
  }

  const lessonNames = (list || []).map((item) => item?.name).filter(Boolean);

  const prompt = `
Analyze the chapter information below and generate relevant tags for this chapter.

Chapter name:
${nameChapter || ''}

Lessons in this chapter:
${JSON.stringify(lessonNames)}

Existing tags:
${JSON.stringify(existingTags)}

Rules:
- Return up to 10 relevant tags.
- Tags must be in ${lang}.
- Use the chapter name and lesson names together to understand the main subject of the chapter.
- Tags should describe the main topics, concepts, skills, technologies, disciplines, or professional areas covered by the chapter.
- Focus on the chapter as a whole rather than generating a tag for every individual lesson.
- Give more importance to topics that appear repeatedly or are central to several lessons.
- Tags must be short and concise.
- Prefer 1-3 words per tag.
- Prefer specific and meaningful terminology over generic terminology.
- Do not duplicate existing tags.
- Do not return near-duplicate synonyms of existing tags.
- Do not return multiple generated tags that mean almost the same thing.
- Avoid generic tags such as:
  chapter, folder, lesson, course, training, learning, education, module, content.
- Do not include hashtags.
- Do not include explanations.
- Return only a valid JSON object in this format:

{
  "tags": ["tag1", "tag2", "tag3"]
}
`;

  try {
    const dataOpenAI = await axios.post(`${config.backend}/openai`, {
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'developer',
          content:
            'You analyze chapter and lesson content and return only valid JSON. The response must contain a key "tags" with an array of strings.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      model: 'gpt-5.4-mini',
      temperature: 0.2,
      max_completion_tokens: 500,
      user: `${authId}`,
    });

    const fullAnswer = dataOpenAI?.data?.choices?.[0]?.message?.content || '';

    const jsonObject = JSON.parse(fullAnswer);

    const tags = Array.isArray(jsonObject)
      ? jsonObject
      : Array.isArray(jsonObject?.tags)
      ? jsonObject.tags
      : [];

    const existingLower = new Set(
      (existingTags || []).map((tag) => String(tag).trim().toLowerCase())
    );

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => !existingLower.has(tag.toLowerCase()))
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting chapter tags:', error);
    return [];
  }
}

export async function getTagsFromLearningPath(
  nameLearningPath,
  list,
  existingTags = [],
  authId
) {
  if (!nameLearningPath && !list?.length) return [];

  let lang;

  if (i18n.language === 'en') {
    lang = 'English';
  } else if (i18n.language === 'fr') {
    lang = 'French';
  } else if (i18n.language === 'es') {
    lang = 'Spanish';
  } else if (i18n.language === 'de') {
    lang = 'German';
  } else {
    lang = 'Dutch';
  }

  const learningPathItems = (list || [])
    .filter((item) => item?.name)
    .map((item) => ({
      type: item?.type || 'unknown',
      name: item.name,
    }));

  const prompt = `
Analyze the learning path information below and generate relevant tags.

Learning path name:
${nameLearningPath || ''}

Learning path content:
${JSON.stringify(learningPathItems)}

The learning path can contain:
- courses
- trainings
- nested learning paths

Existing tags:
${JSON.stringify(existingTags)}

Rules:
- Return up to 10 relevant tags.
- Tags must be in ${lang}.
- Use the learning path name and all included items together to understand the overall subject and purpose of the learning path.
- Consider the type of each item when interpreting the content.
- A "course" represents learning content.
- A "training" may represent training, evaluation, milestone, assessment, practical activity, or another learning activity.
- A "learningpath" represents a nested learning path or learning theme.
- Tags should describe the learning path as a whole, not simply copy the names of individual items.
- Identify recurring topics, concepts, skills, professional areas, technologies, disciplines, or learning objectives across the included content.
- Give more importance to meaningful and recurring subjects.
- Ignore generic or uninformative item names when possible, such as:
  "Untitled training",
  "Untitled course",
  "Untitled learning path",
  "Untitled evaluation",
  "Untitled milestone"
  or equivalent generic names in other languages.
- Do not generate tags based only on words such as "test", "new version", "untitled", or similar placeholder text.
- Tags must be short and concise.
- Prefer 1-3 words per tag.
- Prefer specific and meaningful terminology over generic terminology.
- Do not duplicate existing tags.
- Do not return near-duplicate synonyms of existing tags.
- Do not return multiple generated tags that mean almost the same thing.
- Avoid generic tags such as:
  learning path, course, training, learning, education, lesson, module, content.
- Do not include hashtags.
- Do not include explanations.
- Return only a valid JSON object in this format:

{
  "tags": ["tag1", "tag2", "tag3"]
}
`;

  try {
    const dataOpenAI = await axios.post(`${config.backend}/openai`, {
      response_format: { type: 'json_object' },
      messages: [
        {
          role: 'developer',
          content:
            'You analyze learning paths containing courses, trainings, and nested learning paths. Return only valid JSON with a key "tags" containing an array of strings.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      model: 'gpt-5.4-mini',
      temperature: 0.2,
      max_completion_tokens: 500,
      user: `${authId}`,
    });

    const fullAnswer = dataOpenAI?.data?.choices?.[0]?.message?.content || '';

    const jsonObject = JSON.parse(fullAnswer);

    const tags = Array.isArray(jsonObject)
      ? jsonObject
      : Array.isArray(jsonObject?.tags)
      ? jsonObject.tags
      : [];

    const existingLower = new Set(
      (existingTags || []).map((tag) => String(tag).trim().toLowerCase())
    );

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => !existingLower.has(tag.toLowerCase()))
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting learning path tags:', error);
    return [];
  }
}

function limitLessonText(text, maxLength = 15000) {
  if (!text) return '';

  if (text.length <= maxLength) {
    return text;
  }

  const partLength = Math.floor(maxLength / 3);

  const start = text.slice(0, partLength);

  const middleStart = Math.floor(text.length / 2 - partLength / 2);

  const middle = text.slice(middleStart, middleStart + partLength);

  const end = text.slice(-partLength);

  return `${start}\n\n${middle}\n\n${end}`;
}

export async function getTagsFromLesson(
  nameLesson,
  content,
  existingTags = [],
  authId
) {
  if (!content && !nameLesson) return [];

  const wordCount = content.text.split(/\s+/).filter(Boolean).length;

  const hasEnoughText = wordCount >= 20;
  const hasImages = content.images.length > 0;
  const hasMediaDescription = content.media.length > 0;
  const hasQuestions = content.questions.length > 0;

  if (!hasEnoughText && !hasImages && !hasMediaDescription && !hasQuestions) {
    return [];
  }

  const lessonText = limitLessonText(content.text);

  let lang;

  if (i18n.language === 'en') {
    lang = 'English';
  } else if (i18n.language === 'fr') {
    lang = 'French';
  } else if (i18n.language === 'es') {
    lang = 'Spanish';
  } else if (i18n.language === 'de') {
    lang = 'German';
  } else {
    lang = 'Dutch';
  }

  const prompt = `
Analyze the lesson information below and generate relevant tags.

Lesson name:
${nameLesson || ''}

Lesson text:
${lessonText || 'No meaningful text content available.'}

Questions included in the lesson:
${
  content.questions.length
    ? JSON.stringify(content.questions)
    : 'No questions available.'
}

Media descriptions:
${
  content.media.length
    ? JSON.stringify(content.media)
    : 'No media descriptions available.'
}

Existing tags:
${JSON.stringify(existingTags)}

Some images from the lesson may also be attached to this request.

Use all available information together:
- lesson name
- lesson text
- questions included in the lesson
- media descriptions
- attached images

Questions are important because they indicate which concepts, knowledge,
or skills the learner is expected to understand or demonstrate.

Rules:
- Return up to 10 relevant tags.
- Tags must be in ${lang}.
- Analyze the lesson as a whole.
- Use questions as additional evidence for understanding the main topics and learning objectives.
- Give more importance to topics that appear both in the lesson content and in its questions.
- Do not generate tags merely because a word appears once in a question.
- Tags should describe the main subjects, concepts, skills, technologies, people, theories, methods, equipment, processes, or professional areas covered by the lesson.
- Focus on the overall meaning and main topics of the lesson.
- Give more importance to concepts that are central, repeated, or strongly represented.
- Attached images should influence the tags only when their content is relevant to the lesson.
- Do not generate tags from insignificant visual details such as colors, backgrounds, decorative elements, or generic objects.
- Do not generate tags from incidental or insignificant details.
- Do not simply copy random words from the lesson or questions.
- Prefer specific domain terminology when the lesson clearly concerns a specific subject.
- Tags must be short and concise.
- Prefer 1-3 words per tag.
- Do not duplicate existing tags.
- Do not return near-duplicate synonyms of existing tags.
- Do not return multiple generated tags that mean almost the same thing.
- Avoid generic tags such as:
  lesson, course, training, learning, education, content, information, introduction, question, quiz.
- Do not include hashtags.
- Do not include explanations.
- Return only a valid JSON object in this format:

{
  "tags": ["tag1", "tag2", "tag3"]
}
`;

  try {
    const messageContent = [
      {
        type: 'text',
        text: prompt,
      },

      ...content.images.slice(0, 3).map((url) => ({
        type: 'image_url',
        image_url: {
          url,
        },
      })),
    ];

    const dataOpenAI = await axios.post(`${config.backend}/openai`, {
      response_format: {
        type: 'json_object',
      },

      messages: [
        {
          role: 'developer',
          content:
            'You analyze educational lesson content, including lesson text, questions and images, and return only valid JSON. The response must contain a key "tags" with an array of strings.',
        },
        {
          role: 'user',
          content: messageContent,
        },
      ],

      model: 'gpt-5.4-mini',
      temperature: 0.2,
      max_completion_tokens: 500,
      user: `${authId}`,
    });

    const fullAnswer = dataOpenAI?.data?.choices?.[0]?.message?.content || '';

    const jsonObject = JSON.parse(fullAnswer);

    const tags = Array.isArray(jsonObject)
      ? jsonObject
      : Array.isArray(jsonObject?.tags)
      ? jsonObject.tags
      : [];

    const existingLower = new Set(
      (existingTags || []).map((tag) => String(tag).trim().toLowerCase())
    );

    return tags
      .map((tag) => String(tag).trim())
      .filter(Boolean)
      .filter((tag) => !existingLower.has(tag.toLowerCase()))
      .slice(0, 10);
  } catch (error) {
    console.error('Error getting lesson tags:', error);
    return [];
  }
}
